import { ComplianceTab } from '../types';

interface ComplianceTabsProps {
  activeTab: ComplianceTab;
  onChange: (tab: ComplianceTab) => void;
}

const tabs: {
  key: ComplianceTab;
  title: string;
  icon: string;
  difficulties: string[];
  mechanism: string;
  deliverables: string[];
  boundary: string;
}[] = [
  {
    key: 'customs',
    title: '关务合规',
    icon: 'ri-ship-line',
    difficulties: [
      '要素缺失/逻辑冲突 → 退单返工',
      '单证散落 → 查验准备成本高',
    ],
    mechanism: '提交前校验 + 回执回传留痕 + 单证目录化归档',
    deliverables: [
      '缺资料清单',
      '校验报告',
      '回执&退单原因',
      '归档目录',
    ],
    boundary: '不承诺放行结果，以主管机关回执为准',
  },
  {
    key: 'tax',
    title: '税务合规（9810重点）',
    icon: 'ri-file-list-3-line',
    difficulties: [
      '数据穿透下口径不一致，解释成本高',
      '预退税易，核销链路复杂',
    ],
    mechanism: '三流对齐标红留痕 + 一票一链路固化 + 归档导出',
    deliverables: [
      '三流差异留痕',
      '一票一链路',
      '归档包导出',
      '处理记录',
    ],
    boundary: '不承诺退税结果；退税服务包为叠加项',
  },
  {
    key: 'forex',
    title: '外汇合规',
    icon: 'ri-exchange-dollar-line',
    difficulties: [
      '多笔回款对应关系难解释',
      '财务/关务/业务口径不一致',
    ],
    mechanism: '收款证据链归集 + 权限与导出留痕 + 目录化归档',
    deliverables: [
      '收款关联记录',
      '导出归档包目录',
      '导出留痕',
    ],
    boundary: '不提供外汇审批结果保证',
  },
];

export default function ComplianceTabs({ activeTab, onChange }: ComplianceTabsProps) {
  const currentTab = tabs.find((tab) => tab.key === activeTab) || tabs[0];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Tab 切换器 */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => onChange(tab.key)}
                className={`px-8 py-4 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-[#14B8A6] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.title}
              </button>
            );
          })}
        </div>

        {/* Tab 内容：固定 4 块结构 */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* 块1：常见难点 */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 p-8 rounded-2xl border border-red-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-100 text-red-600">
                <i className="ri-error-warning-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">常见难点</h3>
            </div>
            <ul className="space-y-3">
              {currentTab.difficulties.map((difficulty, index) => (
                <li key={index} className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-500 text-lg mt-0.5 flex-shrink-0"></i>
                  <span className="text-base text-gray-700 leading-relaxed">{difficulty}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* 块2：治理机制 */}
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-8 rounded-2xl border border-teal-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-100 text-teal-600">
                <i className="ri-settings-3-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">治理机制</h3>
            </div>
            <p className="text-base text-gray-700 leading-relaxed font-medium">
              {currentTab.mechanism}
            </p>
          </div>

          {/* 块3：交付物 */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl border border-blue-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                <i className="ri-file-list-2-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">交付物</h3>
            </div>
            <ul className="space-y-3">
              {currentTab.deliverables.map((deliverable, index) => (
                <li key={index} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-lg mt-0.5 flex-shrink-0"></i>
                  <span className="text-base text-gray-700 leading-relaxed">{deliverable}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* 块4：边界说明 */}
          <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-8 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                <i className="ri-shield-check-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">边界说明</h3>
            </div>
            <p className="text-base text-gray-600 leading-relaxed">
              {currentTab.boundary}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}